import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Avatar } from '@/header/Avatar';
import { formatRelativeTime } from '~/utils/dateFormat';

interface NoteItemProps {
  name: string;
  content: string;
  /** ISO instant from the API, not a `Date`: this row renders it relatively. */
  createdAt: string;
  /**
   * `null` when the author's user row is gone — legacy never cascades a note on
   * `delete_user` (W2.7 R9). Both links are dropped in that case; there is
   * nowhere to go, and the caller passes `t('actions.deleted-user')` as `name`.
   */
  authorId: number | null;
  avatarUrl: string;
  onDelete: () => void;
}

export function NoteItem({
  name,
  content,
  createdAt,
  authorId,
  avatarUrl,
  onDelete,
}: NoteItemProps) {
  const { t, i18n } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  // No confirmation dialog, matching legacy: the anchor's `confirm` class
  // (`buddypress/members/single/student-notes.php:82`) is inert — the bound
  // handler `handleStudentNoteDelete` has no `window.confirm`
  // (`main-script.js:1272-1295`), and `BP_Confirm` is not enqueued on the page
  // at all (`grep -ac BP_Confirm` on the live tab = 0). W3.11 R9.
  const handleDelete = () => {
    // Re-entry guard, which legacy gets from `$li.block()` at
    // `main-script.js:1302`: the card only fades, and `opacity: 0` does not
    // stop pointer events, so a second click inside the 300ms would schedule a
    // second DELETE whose 404 paints an error over a delete that worked.
    if (isDeleting) return;
    setIsDeleting(true);
    setTimeout(() => {
      onDelete();
    }, 300);
  };

  const avatar = <Avatar size={36} src={avatarUrl} />;
  const authorLabel = <NoteAuthor>{name}</NoteAuthor>;
  // The author's name is a link INSIDE the sentence, which is where legacy puts
  // it (`group-notes.php:64` substitutes the echoed `bp_core_get_user_domain`
  // anchor into the `%s` of "%s left a note"). `formatString` returns a string
  // and so cannot carry a node, so the template is split on its placeholder and
  // the link rendered in the gap. All four locales are a single leading `%s`, so
  // `before` is empty and word order survives.
  const [before, after] = t('wbs.s-left-a-note').split('%s');

  return (
    <NoteCard $isDeleting={isDeleting}>
      <NoteHeader>
        {/* Legacy's avatar anchor renders `href=""` — `group-notes.php:60`
            calls `bp_core_get_user_domain()` without `echo`. That is a bug, not
            a rule, so the avatar gets the working link the name already has
            (W2.7 D9). Shape copied from `ActivityFeedRow.tsx:44-53`. */}
        {authorId === null ? (
          avatar
        ) : (
          <AvatarLink>
            <Link
              to="/members/$memberId/profile/"
              params={{ memberId: String(authorId) }}
            >
              {avatar}
            </Link>
          </AvatarLink>
        )}
        <NoteAuthorInfo>
          <NoteLabel>
            {before}
            {authorId === null ? (
              authorLabel
            ) : (
              <AuthorLink>
                <Link
                  to="/members/$memberId/profile/"
                  params={{ memberId: String(authorId) }}
                >
                  {authorLabel}
                </Link>
              </AuthorLink>
            )}
            {after}
          </NoteLabel>
          <NoteTime>{formatRelativeTime(createdAt, i18n.language)}</NoteTime>
        </NoteAuthorInfo>
      </NoteHeader>
      <NoteContent>{content}</NoteContent>
      {/* Unconditional, on every note, to every viewer the tab admits — W3.11 D3.
          The author-only rule this row used to take as a prop was invented; the
          legacy citations and the measured cost of keeping it are stated once, at
          the enforcing handler (`student_notes/router.py`,
          `delete_student_note`). */}
      <DeleteButton onClick={handleDelete} disabled={isDeleting}>
        <FontAwesomeIcon icon={faTrashAlt} />
        <DeleteText>{t('actions.delete')}</DeleteText>
      </DeleteButton>
    </NoteCard>
  );
}

const NoteCard = styled.div<{ $isDeleting: boolean }>`
  background: var(--white);
  border-radius: 5px;
  box-shadow: var(--shadow-inset-sm);
  opacity: ${(props) => (props.$isDeleting ? 0 : 1)};
  padding: 10px 10px 17px;
  transition: opacity 0.3s ease-out;
`;

const NoteHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 17px;
`;

const NoteAuthorInfo = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  gap: 5px;
`;

const NoteAuthor = styled.span`
  color: var(--grey-900);
  font-size: 14px;
`;

// The anchor stays a raw `Link` and the styling sits on a wrapper: `styled(Link)`
// erases the router's route-aware typing, so a `params` object is rejected
// outright — `renderActivityParts.tsx:118-134` hit the same wall.
const AvatarLink = styled.span`
  display: flex;

  a {
    display: flex;
  }
`;

const AuthorLink = styled.span`
  a {
    text-decoration: none;
  }

  a:hover ${NoteAuthor},
  a:focus ${NoteAuthor} {
    color: var(--wbs-red);
  }
`;

const NoteLabel = styled.span`
  color: var(--grey-600);
  font-size: 14px;
`;

const NoteTime = styled.span`
  color: var(--grey-500);
  font-size: 12px;
  font-weight: 300;
`;

const NoteContent = styled.div`
  color: var(--grey-800);
  font-size: 15px;
  margin-bottom: 15px;
  margin-left: 50px;
  white-space: pre-wrap;
`;

const DeleteText = styled.span`
  color: var(--grey-600);
  font-family: var(--raleway);
  font-size: 13px;
  font-weight: 400;
  transition: color 0.2s;
`;

const DeleteButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  gap: 6px;
  padding: 0;

  svg {
    color: var(--wbs-red);
    font-size: 14px;
  }

  &:hover {
    ${DeleteText} {
      color: var(--wbs-red);
    }
  }

  &:disabled {
    cursor: default;
  }
`;
